import React, { useState } from "react";
import { TrendingUp, TrendingDown, AlertTriangle, ChefHat, Sparkles, Target, DollarSign, BarChart2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import RecipeAIAdvisor from "./RecipeAIAdvisor";

const CAT_LABELS = {
  starter: "Entrant", main: "Principal", dessert: "Postres",
  drink: "Beguda", side: "Guarnició", sauce: "Salsa", bread: "Pa", other: "Altres",
};

function getMarginColor(margin) {
  if (margin >= 65) return { bg: "bg-emerald-50", text: "text-emerald-700", bar: "#10b981", border: "border-emerald-200", dot: "bg-emerald-500", label: "Alt" };
  if (margin >= 45) return { bg: "bg-amber-50", text: "text-amber-700", bar: "#f59e0b", border: "border-amber-200", dot: "bg-amber-500", label: "Acceptable" };
  return { bg: "bg-red-50", text: "text-red-700", bar: "#ef4444", border: "border-red-200", dot: "bg-red-500", label: "Baix" };
}

function MarginPill({ margin }) {
  const c = getMarginColor(margin);
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${c.bg} ${c.text} border ${c.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {margin.toFixed(1)}% · {c.label}
    </span>
  );
}

export default function CartaDashboard({ recipes }) {
  const [advisorRecipe, setAdvisorRecipe] = useState(null);

  const priced = recipes
    .filter(r => r.selling_price > 0 && r.estimated_cost > 0)
    .map(r => {
      const margin = ((r.selling_price - r.estimated_cost) / r.selling_price) * 100;
      return { ...r, margin };
    });

  if (priced.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
          <BarChart2 className="w-7 h-7 text-gray-400" />
        </div>
        <p className="text-sm font-semibold text-gray-600 mb-1">Sense dades de rendibilitat</p>
        <p className="text-xs text-gray-400 max-w-xs mx-auto">
          Afegeix el cost i preu de venda a les fitxes tècniques per veure la visió global de la carta.
        </p>
      </div>
    );
  }

  const avgMargin = priced.reduce((s, r) => s + r.margin, 0) / priced.length;
  const avgCost = priced.reduce((s, r) => s + r.estimated_cost, 0) / priced.length;
  const highMargin = priced.filter(r => r.margin >= 65);
  const lowMargin = priced.filter(r => r.margin < 45);
  const acceptableMargin = priced.filter(r => r.margin >= 45 && r.margin < 65);
  const sorted = [...priced].sort((a, b) => b.margin - a.margin);
  const top5 = sorted.slice(0, 5);
  const bottom5 = sorted.slice(-Math.min(5, sorted.length)).reverse();

  const chartData = sorted.slice(0, 12).map(r => ({
    name: r.name.length > 13 ? r.name.slice(0, 11) + "…" : r.name,
    margin: parseFloat(r.margin.toFixed(1)),
    color: getMarginColor(r.margin).bar,
  }));

  return (
    <div className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-xl bg-gray-50 flex items-center justify-center">
              <Target className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Marge mitjà</p>
          </div>
          <p className={`text-2xl font-extrabold ${getMarginColor(avgMargin).text}`}>{avgMargin.toFixed(1)}%</p>
          <MarginPill margin={avgMargin} />
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-xl bg-gray-50 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Cost mitjà</p>
          </div>
          <p className="text-2xl font-extrabold text-gray-800">{avgCost.toFixed(2)}€</p>
          <p className="text-[11px] text-gray-400 mt-1">{priced.length} plats analitzats</p>
        </div>

        <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
            </div>
            <p className="text-[11px] font-semibold text-emerald-500 uppercase tracking-wide">Marge alt</p>
          </div>
          <p className="text-2xl font-extrabold text-emerald-700">{highMargin.length}</p>
          <p className="text-[11px] text-emerald-500 mt-1">plat{highMargin.length !== 1 ? "s" : ""} ≥65%</p>
        </div>

        <div className={`rounded-2xl border p-4 ${lowMargin.length > 0 ? "bg-red-50 border-red-100" : "bg-white border-gray-100"}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${lowMargin.length > 0 ? "bg-red-100" : "bg-gray-50"}`}>
              <AlertTriangle className={`w-4 h-4 ${lowMargin.length > 0 ? "text-red-500" : "text-gray-400"}`} />
            </div>
            <p className={`text-[11px] font-semibold uppercase tracking-wide ${lowMargin.length > 0 ? "text-red-400" : "text-gray-400"}`}>Per revisar</p>
          </div>
          <p className={`text-2xl font-extrabold ${lowMargin.length > 0 ? "text-red-600" : "text-gray-800"}`}>{lowMargin.length}</p>
          <p className={`text-[11px] mt-1 ${lowMargin.length > 0 ? "text-red-400" : "text-gray-400"}`}>plat{lowMargin.length !== 1 ? "s" : ""} &lt;45%</p>
        </div>
      </div>

      {/* Chart + Best/Worst */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-4">Marge per plat (%)</h3>
          <ResponsiveContainer width="100%" height={175}>
            <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} domain={[0, 100]} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                formatter={(v) => [`${v}%`, "Marge"]}
              />
              <Bar dataKey="margin" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, i) => <Cell key={`entry-${i}`} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Segment summary */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-4">Distribució carta</h3>
          <div className="space-y-3">
            {[
              { label: "Marge alt", count: highMargin.length, color: "bg-emerald-500", pct: priced.length > 0 ? highMargin.length / priced.length * 100 : 0 },
              { label: "Acceptable", count: acceptableMargin.length, color: "bg-amber-400", pct: priced.length > 0 ? acceptableMargin.length / priced.length * 100 : 0 },
              { label: "Marge baix", count: lowMargin.length, color: "bg-red-400", pct: priced.length > 0 ? lowMargin.length / priced.length * 100 : 0 },
            ].map(s => (
              <div key={s.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600 font-medium">{s.label}</span>
                  <span className="text-gray-500 font-bold">{s.count} plats</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${s.color}`} style={{ width: `${s.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top / Bottom dishes */}
      <div className="grid sm:grid-cols-2 gap-4">
        {/* Most profitable */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-50 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            <h3 className="text-sm font-bold text-gray-800">Plats més rendibles</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {top5.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 px-5 py-3">
                <span className="text-[11px] font-bold text-gray-300 w-4">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{r.name}</p>
                  <p className="text-[11px] text-gray-400">{CAT_LABELS[r.category]} · Cost: {r.estimated_cost.toFixed(2)}€ · PVP: {r.selling_price.toFixed(2)}€</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm font-extrabold text-emerald-600">{r.margin.toFixed(1)}%</span>
                  <button
                    onClick={() => setAdvisorRecipe(r)}
                    className="p-1 rounded-lg hover:bg-[#f9f0f2] text-[#7B2D3F] transition-colors"
                    title="Anàlisi IA"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Least profitable */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-50 flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-red-400" />
            <h3 className="text-sm font-bold text-gray-800">Plats menys rendibles</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {bottom5.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 px-5 py-3">
                <span className="text-[11px] font-bold text-gray-300 w-4">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{r.name}</p>
                  <p className="text-[11px] text-gray-400">{CAT_LABELS[r.category]} · Cost: {r.estimated_cost.toFixed(2)}€ · PVP: {r.selling_price.toFixed(2)}€</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <MarginPill margin={r.margin} />
                  <button
                    onClick={() => setAdvisorRecipe(r)}
                    className="p-1 rounded-lg hover:bg-[#f9f0f2] text-[#7B2D3F] transition-colors"
                    title="Anàlisi IA"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Optimization tips for low margin dishes */}
      {lowMargin.length > 0 && (
        <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-red-800">{lowMargin.length} plat{lowMargin.length !== 1 ? "s" : ""} amb marge baix</p>
              <p className="text-xs text-red-600 mt-0.5">Revisa aquests plats per millorar la rendibilitat de la carta</p>
            </div>
          </div>
          <div className="space-y-2">
            {lowMargin.map(r => {
              const targetPvp = (r.estimated_cost / 0.35).toFixed(2);
              return (
                <div key={r.id} className="bg-white rounded-xl p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{r.name}</p>
                    <p className="text-xs text-red-600">Marge actual: {r.margin.toFixed(1)}% · PVP recomanat (65%): <strong>{targetPvp}€</strong></p>
                  </div>
                  <button
                    onClick={() => setAdvisorRecipe(r)}
                    className="flex items-center gap-1.5 text-[11px] font-semibold text-[#7B2D3F] bg-[#f9f0f2] hover:bg-[#f0e0e5] px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
                  >
                    <Sparkles className="w-3 h-3" /> Analitzar IA
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {advisorRecipe && <RecipeAIAdvisor recipe={advisorRecipe} onClose={() => setAdvisorRecipe(null)} />}
    </div>
  );
}